<script>

	import { onMount, onDestroy, tick } from 'svelte';
	import { slide } from "svelte/transition";

	import Unlock from './Unlock.svelte';

	import Panel from './ui/Panel.svelte';
	import Field from './ui/Field.svelte';
	import Button from './ui/Button.svelte';
	import ButtonGroup from './ui/ButtonGroup.svelte';
	import Range from "./ui/Range.svelte";

	import { settings, showSettings, event, deviceRef, deviceName, offline, remSize, unlockCode, appElement, session, spotlight } from './lib/stores.js';
	import { getServerData, postServerData } from './lib/prelude.js';
	import { getUA } from './lib/device.js';
	import { db } from "./lib/db.js";

	let unlocked = $unlockCode ? true : false;
	let workingData = null;
	let mounted = false;
	let dataOK = true; // we may need to check workingData?
	let workingRemSize;

	const ua = getUA();

	async function setWorkingData() {

		console.log('setWorkingData', $settings);

		workingData = JSON.parse(JSON.stringify($settings));

		if (!workingData.deviceName) {
			workingData.deviceName = $deviceName;
			await checkDeviceName();
		}

		if (!workingData.mode) {
			workingData.mode = 'moderator';
		}

		workingRemSize = $remSize.replace(/px$/, '');

		// if changes are forced or we're loading a new event, insta-save
		await saveSettings();

		mounted = true;

	}

	async function saveSettings() {

		const json = JSON.stringify(workingData);

		$settings = JSON.parse(json);
		$deviceName = workingData.deviceName;

		postServerData('/onstage/device', {
			settings: json
		});

		$remSize = workingRemSize + 'px';

	}

	async function saveAndClose() {
		await saveSettings();
		$showSettings = false;
	}

	async function checkDeviceName() {
		// console.log('checkDeviceName',workingData.deviceName);
		if (workingData.deviceName == '') {
			const d = ua + ' ' + $deviceRef.substring(4,8);
			// console.log('checkDeviceName --> ',d);
			await tick();
			workingData.deviceName = d;
		}
	}

	$: if (unlocked && $event) {
		setWorkingData();
	}

	$: if (workingData) {
		checkDeviceName();
	}

	onDestroy(() => {
		setRemSize($remSize);
	});

	let remTimer;

	function setRemSize(rs) {
		clearTimeout(remTimer);
		setTimeout(() => {
			document.documentElement.style.fontSize = rs + 'px';
		}, 100);
	}

	$: setRemSize(workingRemSize);

	function lockDevice() {

		$appElement.classList.add('fade');

		setTimeout(() => {
			$unlockCode = undefined;
			$showSettings = false;
			$session = null;
			$spotlight = null;
			setTimeout(() => {
				$appElement.classList.remove('fade');
			}, 400);
		}, 400);

	}

</script>

<style>
	.actions {
		padding-left: 2rem;
		padding-right: 2rem;
		padding-bottom: 1.4rem;
	}

	.field {
		padding: 1rem 0;
		display: flex;
		gap: 0.8rem 2rem;
		flex-wrap: wrap;
	}

	.labelgroup {
		/*flex: 0 0 16rem;*/
		flex: 0 0 100%;
		font-size: 1rem;
		padding: 0;
		margin: 0;
		display: flex;
		justify-content: space-between;
		line-height: 1;
		color: var(--labelColor);
	}

	@media all and (min-width: 800px) {
		.labelgroup {
			padding-top: 0.75rem;
		}
	}

	.inputwrapper {
		flex: 1 0 55%;
		/*min-width: 55%;*/
		max-width: 100%;
	}

	.modes {
		display: flex;
		gap: 1rem;
		margin-bottom: 1rem;
	}

	.modes button {
		flex: 1 0 auto;
		border: 1px solid var(--blend-40);
		color: var(--blend-40);
		border-radius: 4px;
		padding: 0.6rem;
		background: transparent;
		text-align: center;
		font-size: 0.9rem;
	}

	.modes button.selected {
		color: var(--bgColor);
		border: 1px solid var(--accentColor);
		background: var(--accentColor);
	}

	.notes {
		font-size: 0.8rem;
		color: var(--blend-60);
		margin: -1rem 0 0.8rem 0;
		max-width: 40rem;
	}

	.range {
		margin: 2rem 0;
		font-size: 1rem;
		line-height: 1;
		color: var(--blend-90);
		display: flex;
		gap: 0.8rem 2rem;
		flex-wrap: wrap;
	}

	.range > p {
		/*flex: 0 0 16rem;*/
		flex: 0 0 100%;
	}

	.range > div {
		display: flex;
		align-items: center;
		margin: 0.5rem 0 0 0;
		gap: 1rem;
		/*flex: 1 0 55%;*/
		flex: 0 1 30rem;
	}

	/*@media all and (min-width: 767px) {
		.range > div {
			margin: -0.15rem 0 0 0;
		}
	}*/

	.range .sm {
		font-size: 14px;
		color: var(--blend-60);
		line-height: 1;
		width: 20px;
		text-align: center;
	}

	.range .lg {
		font-size: 19px;
		color: var(--blend-60);
		line-height: 1;
		width: 20px;
		text-align: center;
	}
</style>

{#if unlocked && workingData}
	<Panel title="Settings for this device" on:close={() => { $showSettings = false }}>

		<form id="settingsForm" on:submit|preventDefault={saveAndClose} style="--font: 'Inter', sans-serif;">
			{#if mounted}

				<Field
					bind:value={workingData.deviceName}
					component="TextField"
					label="Device name"
				/>

				{#if $event.setup.app && $event.setup.app.onstage && $event.setup.app.onstage.rooms}
					<Field
						bind:value={workingData.room}
						component="SelectList"
						label="Room"
						options={$event.setup.app.onstage.rooms}
						allowNull={true}
						nullOption="None"
					/>
				{/if}

				{#if $event.setup.app && $event.setup.app.onstage && $event.setup.app.onstage.rooms && workingData.room}
					<div transition:slide|local={{ duration: 300 }}>
						<div class="field">
							<p class="labelgroup">Mode</p>
							<div class="inputwrapper">
								<div class="modes">
									<button
										type="button"
										class:selected={workingData.mode == 'moderator'}
										on:click={() => { workingData.mode = 'moderator' }}
									>
										<span>Moderator</span>
									</button>
									<button
										type="button"
										class:selected={workingData.mode == 'presenter'}
										on:click={() => { workingData.mode = 'presenter' }}
									>
										<span>Presenter</span>
									</button>
									<button
										type="button"
										class:selected={workingData.mode == 'av'}
										on:click={() => { workingData.mode = 'av' }}
									>
										<span>Screen AV</span>
									</button>
								</div>
							</div>
							{#if workingData.mode == 'moderator'}
								<p class="notes">
									{#if $event.setup.app.questionModeration}
										Filter audience questions (accept / reject), create and manage live polls and manage chat posts. Use this mode to control all audience interactions.
									{:else}
										Manage audience questions, create and manage live polls and manage chat posts. Use this mode to control all audience interactions.
									{/if}
								</p>
							{:else if workingData.mode == 'presenter'}
								<p class="notes">
									{#if $event.setup.app.questionModeration}
										View accepted questions and manage (open and close) live polls. Use this mode to forward the moderated questions to the presenter / host on stage.
									{:else}
										View questions and manage (open and close) live polls. Use this mode for a presenter / host on stage.
									{/if}
								</p>
							{:else if workingData.mode == 'av'}
								<p class="notes">Passive mode that only displays content you add to the screen using Moderator or Presenter mode on another device. Only use this mode for devices that are directly connected to the projector or AV desk.</p>
							{/if}
						</div>
					</div>

					{#if workingData.mode != 'av'}
						<div transition:slide|local={{ duration: 300 }}>
							<Field
								bind:value={workingData.displaySpotlight}
								component="Switch"
								label="Allow this device to display on-screen content?"
								columns={false}
							/>
						</div>
					{/if}
				{/if}

				<div class="range">
					<p><label for="textSize">Text size on this device</label></p>
					<div>
						<span class="sm">A</span>
						<Range
							ref="textSize"
							bind:value={workingRemSize}
							min={15}
							max={18}
						/>
						<span class="lg">A</span>
					</div>
				</div>

			{/if}
		</form>

		<div class="actions" slot="actions">
			<ButtonGroup wide={false} center={false}>
				<Button type="submit" form="settingsForm" label="Save settings" double={true} green={true} disabled={!dataOK}>
					<svg viewBox="0 0 80 80"><path d="M28.8 59.6l-12.6-12.6 4.3-4.2 8.3 8.3 30.7-30.7 4.3 4.2z"/></svg>
				</Button>
				<Button type="button" ghost={true} label="Lock device" double={true} on:click={lockDevice}>
					<svg viewBox="0 0 80 80"><path d="m44 45.99c0-2.21-1.79-4-4-4s-4 1.79-4 4c0 1.48.81 2.75 2 3.45v5.53c0 1.1.9 2 2 2s2-.9 2-2v-5.53c1.19-.7 2-1.97 2-3.45z"/><path d="m57 34.01h-3.01v-5.04c0-7.72-6.27-14-13.99-14s-13.99 6.28-13.99 14v5.04h-3.01c-2.21 0-4 1.79-4 4v22.98c0 2.21 1.79 4 4 4h34c2.21 0 4-1.79 4-4v-22.98c0-2.21-1.79-4-4-4zm-26.99-5.04c0-5.51 4.48-10 9.99-10s9.99 4.49 9.99 10v5.04h-19.98zm26.99 30.03c0 1.1-.9 2-2 2h-30c-1.1 0-2-.9-2-2v-19c0-1.1.9-2 2-2h30c1.1 0 2 .9 2 2z"/></svg>
				</Button>
			</ButtonGroup>
		</div>

	</Panel>
{:else}
	<Panel on:close={() => { $showSettings = false }}>
		<Unlock bind:unlocked on:reset/>
	</Panel>
{/if}
